import type * as zod from 'zod';

import type { CartesianPose } from '@sb/geometry';
import { posesAreEqual } from '@sb/geometry';
import type { TCPOffsetOption } from '@sb/motion-planning';
import { ArmTarget, DEFAULT_TCP_OFFSET_OPTION } from '@sb/motion-planning';
import type { StepFailure } from '@sb/routine-runner';
import { FailureKind } from '@sb/routine-runner';

import Step from '../Step';

import Arguments from './Arguments';
import Variables from './Variables';

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

export default class ArcStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    this.variables = {};
  }

  /**
   * Placeholder method for the play logic.
   * The play method is not used directly for the arc step.
   */
  public async _play(): Promise<void> {
    // NO _play method for ArcStep. Use getArmTarget with moveArmToV2 or Weld.
  }

  private static overwriteOrientation(
    pose: CartesianPose,
    orientationSource: CartesianPose,
  ): CartesianPose {
    return {
      ...pose,
      i: orientationSource.i,
      j: orientationSource.j,
      k: orientationSource.k,
      w: orientationSource.w,
    };
  }

  public async getArmTarget({
    effectiveTCPOption = undefined,
    parentCompletedCount,
    previousTargetPose,
  }: {
    effectiveTCPOption?: TCPOffsetOption;
    parentCompletedCount: number;
    previousTargetPose?: CartesianPose;
  }): Promise<
    | {
        target: ArmTarget[];
        poseForPreviousTarget: CartesianPose;
        poseForPreviousTargetTCPOption: TCPOffsetOption | undefined;
      }
    | StepFailure
  > {
    const resultA = await this._getArmTarget({
      index: 0,
      effectiveTCPOption,
      parentCompletedCount,
    });

    const resultB = await this._getArmTarget({
      index: 1,
      effectiveTCPOption,
      parentCompletedCount,
    });

    if ('failure' in resultA) {
      return resultA;
    }

    if ('failure' in resultB) {
      return resultB;
    }

    // When maintainStartOrientation is enabled, overwrite the via and end
    // orientations with the start (previous target) orientation. This ensures
    // the poses sent to the planner match, and that poseForPreviousTarget
    // carries the correct orientation for the next segment.
    if (this.args.maintainStartOrientation && previousTargetPose) {
      const targetA = resultA.target;
      const targetB = resultB.target;

      if ('pose' in targetA) {
        targetA.pose = ArcStep.overwriteOrientation(
          targetA.pose,
          previousTargetPose,
        );
      }

      if ('pose' in targetB) {
        targetB.pose = ArcStep.overwriteOrientation(
          targetB.pose,
          previousTargetPose,
        );
      }

      return {
        target: [targetA, targetB],
        poseForPreviousTarget: ArcStep.overwriteOrientation(
          resultB.poseForPreviousTarget,
          previousTargetPose,
        ),
        poseForPreviousTargetTCPOption: resultB.poseForPreviousTargetTCPOption,
      };
    }

    return {
      target: [resultA.target, resultB.target],
      poseForPreviousTarget: resultB.poseForPreviousTarget,
      poseForPreviousTargetTCPOption: resultB.poseForPreviousTargetTCPOption,
    };
  }

  public async _getArmTarget({
    index,
    effectiveTCPOption = undefined,
    parentCompletedCount,
  }: {
    index: number;
    effectiveTCPOption?: TCPOffsetOption;
    parentCompletedCount: number;
  }): Promise<
    | {
        target: ArmTarget;
        poseForPreviousTarget: CartesianPose;
        poseForPreviousTargetTCPOption: TCPOffsetOption | undefined;
      }
    | StepFailure
  > {
    // TODO Implement getArmTarget for ArcStep. Current implementation is a placeholder that just moves us to the "end" of the arc.
    const argsTarget = this.args.targets[index];

    const parseTarget = (armPosition: any) => {
      const target = {
        motionKind: 'arc',
        ...armPosition,
        calibrationPose: armPosition.pose,
        blendRadius: 0,
        stopHere: this.args.stopHere,
        maintainStartOrientation: this.args.maintainStartOrientation,
      };

      // remove jointAngle fields
      target.jointAngles = undefined;
      target.calibrationPose = undefined;

      const pose = { ...armPosition.pose };

      // set effectiveTCPOption
      if (effectiveTCPOption) {
        // weld waypoints use the effectiveTCPOption from the parent weld step
        target.effectiveTCPOption = effectiveTCPOption;
      } else if (this.args.tcpOption === 'auto') {
        if ('tcpOption' in target && target.tcpOption) {
          // use tcpOption that target was originally created with
          // we can leave target.tcpOption as is.
          target.effectiveTCPOption = target.tcpOption;
        } else {
          // use default tcpOption
          target.effectiveTCPOption = DEFAULT_TCP_OFFSET_OPTION;
        }
      } else {
        // use tcpOption that was specified in the Waypoint step
        target.effectiveTCPOption = this.args.tcpOption;
      }

      const parsedTarget = ArmTarget.safeParse(target);

      if (!parsedTarget.success) {
        throw new Error('Move arm target is not valid');
      }

      return { target: parsedTarget.data, pose };
    };

    let target: ArmTarget;
    let anchor: any; // Space Item
    let poseForPreviousTarget: CartesianPose;

    try {
      if ('positionListID' in argsTarget) {
        const parseResult = parseTarget(
          this.routineContext.getPositionListEntry(
            argsTarget.positionListID,
            argsTarget.positionListIndex ?? parentCompletedCount,
          ),
        );

        const targetSpace = this.routineContext.getSpaceItem(
          argsTarget.positionListID,
        );

        target = parseResult.target;
        poseForPreviousTarget = parseResult.pose;

        if (targetSpace.anchoredToID) {
          anchor = this.routineContext.getSpaceItem(targetSpace.anchoredToID);
        }
      } else if ('expression' in argsTarget) {
        const expressionTarget = await this.routineContext.evaluateExpression(
          argsTarget.expression,
        );

        // if expression evaluates to an array, use the first item
        // this makes it easier to use 'SinglePosition' space objects.
        const parseResult = parseTarget(
          Array.isArray(expressionTarget)
            ? expressionTarget[0]
            : expressionTarget,
        );

        target = parseResult.target;
        poseForPreviousTarget = parseResult.pose;
      } else {
        const parseResult = parseTarget(argsTarget);
        target = parseResult.target;
        poseForPreviousTarget = parseResult.pose;
      }

      if (
        'positionListID' in argsTarget &&
        anchor &&
        anchor.kind === 'localAccuracyCalibration'
      ) {
        if ('pose' in target) {
          target.pose = this.routineContext.getCalibratedGoal(
            target.pose,
            anchor,
          );
        } else if ('jointAngles' in target && 'calibrationPose' in target) {
          const calibratedPose = this.routineContext.getCalibratedGoal(
            target.calibrationPose,
            anchor,
          );

          // do not change the original target object if pose did not change
          if (!posesAreEqual(target.calibrationPose, calibratedPose)) {
            const newJointAngles =
              await this.routineContext.getJointAnglesForCartesianSpacePose(
                calibratedPose,
                'joint',
                false,
              );

            if (newJointAngles) {
              target.jointAngles = newJointAngles;
            }
          }
        }
      }

      return {
        target,
        poseForPreviousTarget,
        // The TCP frame this segment commanded (the parent reads it as the carry tag).
        poseForPreviousTargetTCPOption: (
          target as { effectiveTCPOption?: TCPOffsetOption }
        ).effectiveTCPOption,
      };
    } catch (error) {
      return {
        failure: {
          kind: FailureKind.InvalidRoutineLoadedFailure,
        },
        failureReason: error.message,
        error,
      };
    }
  }

  public hasStopEarlyConditions(): boolean {
    return false;
  }

  public async evaluateStopEarlyCondition(): Promise<boolean> {
    return false;
  }
}
